import {
  DataProvider,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
  CreateParams,
  CreateResult,
  Identifier,
  DeleteParams,
  DeleteResult,
} from 'react-admin'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import { EngineMediaActionDto } from '../../dto/engine-media/engine-media-action.dto'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import filterMapper from './engine-media-action-filter.mapper'
import { mapSortEngineMediaActionParam } from './engine-media-action-sort.mapper'
import {
  get,
  getWithPagination,
  patch,
  post,
  remove,
} from '../../common/fetch.utils'
import { ENGINE_MEDIA_ACTIONS_URL } from '../../api-urls'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<EngineMediaActionDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortEngineMediaActionParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<EngineMediaActionDto[]>(
      `${ENGINE_MEDIA_ACTIONS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<EngineMediaActionDto>> => {
    const data = await get<EngineMediaActionDto>(
      ENGINE_MEDIA_ACTIONS_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<EngineMediaActionDto>> => {
    const { data } = await getWithPagination<EngineMediaActionDto[]>(
      `${ENGINE_MEDIA_ACTIONS_URL}/GetByConditions/`,
      `e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateEngineMediaActionRequest>,
  ): Promise<CreateResult<EngineMediaActionDto>> => {
    const action = await post<
      CreateEngineMediaActionRequest[],
      EngineMediaActionDto[]
    >(ENGINE_MEDIA_ACTIONS_URL, [data])
    return {
      data: action[0],
    }
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<EngineMediaActionDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      EngineMediaActionDto
    >(ENGINE_MEDIA_ACTIONS_URL, {
      id,
      mediaAction:
        data.mediaAction !== previousData.mediaAction
          ? data.mediaAction
          : undefined,
      name: data.name !== previousData.name ? data.name : undefined,
    })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<DeleteResult> => {
    await remove<undefined>(ENGINE_MEDIA_ACTIONS_URL, `/Delete/${id}`)
    return Promise.resolve({ data: { id } })
  },
} as DataProvider

export interface CreateEngineMediaActionRequest {
  readonly mediaAction: number
  readonly name: string
}

export default provider
